import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  DocumentTemplatesActions,
  ItemActions,
  selectTemplateData,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { UserApproval } from '@qtek/shared/models';
import { isNonNullable } from '@qtek/shared/utils';
import { QuillModule } from 'ngx-quill';
import { filter, fromEvent, map, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DocumentTemplatesModule } from './document-templates.module';

export interface NdaDialogData {
  participantId: string;
  title?: string;
  dealId: string;
  templateId?: string;
  templateDataId: string;
}

export interface NdaActionResult {
  action: UserApproval | 'back';
  dealId: string | null;
  participantId: string | null;
}

@Component({
  selector: 'qt-nda-dialog',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    QuillModule,
    AsyncPipe,
    DocumentTemplatesModule,
    TranslationCoreModule,
    MatIconModule,
  ],
  templateUrl: './nda-dialog.component.html',
  styleUrls: ['./nda-dialog.component.scss'],
})
export class NdaDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentArea', { static: false })
  contentArea!: ElementRef<HTMLDivElement>;

  readonly title: string;
  readonly dealId: string | null;
  readonly participantId: string | null;
  readonly templateId: string | null;

  isLoading = true;
  isScrolledToBottom = false;
  isProcessingAction = false;
  ndaTemplate$: Observable<string | null>;

  private readonly destroy$ = new Subject<void>();
  private readonly dialogRef = inject(MatDialogRef<NdaDialogComponent>);
  private readonly data = inject<NdaDialogData>(MAT_DIALOG_DATA);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  constructor() {
    const queryParams = this.route.snapshot.queryParams;

    this.participantId =
      this.data?.participantId || queryParams['participantId'] || null;
    this.dealId = this.data?.dealId;
    this.templateId = this.data?.templateId;
    this.title = this.data?.title || 'GNR_CONF_AGREEMENT';
  }

  ngOnInit(): void {
    this.initializeTemplate();
    this.setupActionListeners();

    this.dialogRef.disableClose = true;
  }

  ngAfterViewInit(): void {
    this.ndaTemplate$.pipe(takeUntil(this.destroy$)).subscribe(template => {
      if (template && this.contentArea) {
        setTimeout(() => {
          this.setupScrollDetection();
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeTemplate(): void {
    const id = this.data.templateDataId;

    this.store.dispatch(
      DocumentTemplatesActions.connectTemplateData({
        payload: {
          prms: { id },
          s: 'model',
          tmpId: this.templateId,
        },
      })
    );

    this.store.dispatch(
      DocumentTemplatesActions.getTemplateData({ id, dealId: this.dealId })
    );

    this.ndaTemplate$ = this.store.select(selectTemplateData).pipe(
      filter(isNonNullable),
      map(data => {
        if (data?.bdy) {
          this.isLoading = false;
        }
        return data?.bdy || null;
      })
    );
  }

  private setupScrollDetection(): void {
    if (!this.contentArea) {
      return;
    }

    const scrollElement = this.contentArea.nativeElement;

    setTimeout(() => {
      const isScrollable =
        scrollElement.scrollHeight > scrollElement.clientHeight;

      if (!isScrollable) {
        this.isScrolledToBottom = true;
        return;
      }

      fromEvent(scrollElement, 'scroll')
        .pipe(debounceTime(300), takeUntil(this.destroy$))
        .subscribe(() => {
          this.checkScrollPosition();
        });

      this.checkScrollPosition();
    }, 500);
  }

  private checkScrollPosition(): void {
    if (!this.contentArea) {
      return;
    }

    if (this.isScrolledToBottom) {
      return;
    }

    const element = this.contentArea.nativeElement;
    const threshold = 10;
    const isAtBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - threshold;

    if (isAtBottom) {
      this.isScrolledToBottom = true;
    }
  }

  public action(userApproval: UserApproval): void {
    if (this.isProcessingAction) return;

    this.isProcessingAction = true;
    this.store.dispatch(
      ItemActions.sendNdaEvent({
        parId: this.participantId,
        userApproval,
      })
    );
  }

  public back(): void {
    this.closeDialog('back');
  }

  private setupActionListeners(): void {
    this.actions$
      .pipe(
        ofType(
          ItemActions.sendNdaEventSuccess,
          ItemActions.sendNdaEventFailure
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(action => {
        this.isProcessingAction = false;
        if (action.type === ItemActions.sendNdaEventSuccess.type) {
          const result: NdaActionResult = {
            action: action.payload.ndas.ndaEvent.userApproval,
            dealId: this.dealId,
            participantId: this.participantId,
          };
          this.dialogRef.close(result);
        } else {
          console.error('NDA action failed:', action);
          this.isProcessingAction = false;
        }
      });
  }

  private closeDialog(action: NdaActionResult['action']): void {
    const result: NdaActionResult = {
      action,
      dealId: this.dealId,
      participantId: this.participantId,
    };

    this.dialogRef.close(result);
  }
}

export const ndaDialogConfig = {
  width: 'min(1200px, 90vw)',
  height: 'auto',
  maxHeight: '90vh',
  disableClose: false,
  autoFocus: false,
  panelClass: 'nda-dialog-panel',
};
